<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Documents</h5>
        <p class="page-sub">Company documents published for you</p>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search documents…" @input="debounceSearch" />
      <select v-model="categoryId" class="filter-select" @change="resetAndFetch">
        <option value="">All Categories</option>
        <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
      </select>
      <select v-model="readFilter" class="filter-select" @change="resetAndFetch">
        <option value="">All</option>
        <option value="unread">Unread</option>
        <option value="read">Read</option>
      </select>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div class="sk-row-head"></div>
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!allDocuments.length" class="empty-box">
      <div class="empty-illustration"><font-awesome-icon icon="file-alt" class="empty-icon" /></div>
      <p class="empty-title">No documents found</p>
      <p class="empty-sub">No documents have been published for you yet.</p>
    </div>

    <!-- Document grid -->
    <div v-else>
      <!-- Pending acknowledgement alert -->
      <div v-if="pendingAckDocs.length" class="ack-alert">
        <font-awesome-icon icon="exclamation-circle" />
        <span>You have <strong>{{ pendingAckDocs.length }}</strong> document(s) requiring your acknowledgement.</span>
      </div>

      <div class="doc-grid">
        <div
          v-for="doc in visibleDocuments"
          :key="doc.id"
          class="doc-card"
          :class="{ 'doc-card--unread': !doc.is_read }"
          @click="openDoc(doc)"
        >
          <div class="doc-card-icon" :class="iconBg(doc.file_type)">
            <font-awesome-icon :icon="fileIcon(doc.file_type)" />
          </div>
          <div class="doc-card-body">
            <div class="doc-card-title">{{ doc.title }}</div>
            <div class="doc-card-meta">
              <span v-if="doc.category" class="doc-category">{{ doc.category?.name }}</span>
              <span class="doc-ver">v{{ doc.version }}</span>
            </div>
            <div class="doc-card-date">{{ fmtDate(doc.published_at) }}</div>
          </div>
          <div class="doc-card-badges">
            <span v-if="!doc.is_read" class="badge badge--blue">New</span>
            <span v-if="doc.requires_acknowledgement && !doc.is_acknowledged" class="badge badge--amber">Needs Ack</span>
            <span v-if="doc.requires_acknowledgement && doc.is_acknowledged" class="badge badge--green">Acknowledged</span>
            <span v-if="isExpired(doc.expires_at)" class="badge badge--red">Expired</span>
          </div>
        </div>
      </div>

      <div class="pagination-bar" v-if="meta.last_page > 1">
        <span class="pg-total">{{ meta.total }} document{{ meta.total !== 1 ? 's' : '' }}</span>
        <div class="pg-controls">
          <button class="pg-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)" title="Previous">‹</button>
          <template v-for="p in paginationPages" :key="p ?? ('ellipsis-' + Math.random())">
            <span v-if="p === null" class="pg-ellipsis">…</span>
            <button
              v-else
              class="pg-btn"
              :class="{ 'pg-btn--active': p === meta.current_page }"
              @click="goPage(p)"
            >{{ p }}</button>
          </template>
          <button class="pg-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)" title="Next">›</button>
        </div>
      </div>
    </div>

    <!-- ══  Document Viewer Modal  ══ -->
    <div class="modal-overlay" v-if="viewer.open" @click.self="viewer.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <div class="modal-head-left">
            <span class="modal-doc-icon" :class="iconBg(viewer.doc?.file_type)">
              <font-awesome-icon :icon="fileIcon(viewer.doc?.file_type)" />
            </span>
            <div>
              <p class="modal-title">{{ viewer.doc?.title }}</p>
              <p class="modal-subtitle">v{{ viewer.doc?.version }} &nbsp;·&nbsp; {{ viewer.doc?.category?.name || 'Uncategorised' }}</p>
            </div>
          </div>
          <button class="modal-close" @click="viewer.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" v-if="viewer.doc">
          <!-- Doc details grid -->
          <div class="detail-grid">
            <div class="detail-item"><span class="detail-label">Published</span><span class="detail-value">{{ fmtDate(viewer.doc.published_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Expires</span><span class="detail-value" :class="{ 'text-danger': isExpired(viewer.doc.expires_at) }">{{ fmtDate(viewer.doc.expires_at) }}</span></div>
            <div class="detail-item"><span class="detail-label">Read Status</span><span class="badge" :class="viewer.doc.is_read ? 'badge--green' : 'badge--gray'">{{ viewer.doc.is_read ? 'Read' : 'Unread' }}</span></div>
            <div class="detail-item" v-if="viewer.doc.requires_acknowledgement">
              <span class="detail-label">Acknowledgement</span>
              <span class="badge" :class="viewer.doc.is_acknowledged ? 'badge--green' : 'badge--amber'">
                {{ viewer.doc.is_acknowledged ? 'Acknowledged' : 'Pending' }}
              </span>
            </div>
          </div>

          <!-- Description -->
          <div v-if="viewer.doc.description" class="desc-box">
            <p class="desc-content">{{ viewer.doc.description }}</p>
          </div>

          <!-- Actions -->
          <div class="viewer-actions">
            <button v-if="viewer.doc.file_name" class="btn-primary-action" @click="downloadDoc(viewer.doc)">
              <font-awesome-icon icon="download" /> Download {{ viewer.doc.file_name }}
            </button>
            <button v-if="!viewer.doc.is_read" class="btn-outline" @click="markRead(viewer.doc)">
              <font-awesome-icon icon="check" /> Mark as Read
            </button>
          </div>

          <!-- Acknowledgement -->
          <div v-if="viewer.doc.requires_acknowledgement && !viewer.doc.is_acknowledged" class="ack-box">
            <p class="ack-title"><font-awesome-icon icon="signature" /> Acknowledgement Required</p>
            <p class="ack-body">By clicking "Acknowledge", you confirm that you have read and understood this document.</p>
            <textarea v-model="viewer.ackNote" class="form-input" rows="2" placeholder="Optional note (e.g. questions or comments)…"></textarea>
            <button class="btn-primary-action" :disabled="viewer.acking" style="margin-top:10px" @click="acknowledge(viewer.doc)">
              {{ viewer.acking ? 'Processing…' : 'I Acknowledge This Document' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyDocumentsPage',

  data() {
    return {
      loading:    false,
      documents:  [],
      categories: [],
      meta: { current_page: 1, last_page: 1, total: 0 },
      page: 1,
      search: '', categoryId: '', readFilter: '',
      searchTimer: null,

      viewer: { open: false, doc: null, ackNote: '', acking: false },
    };
  },

  computed: {
    allDocuments() { return this.documents; },
    visibleDocuments() {
      if (!this.readFilter) return this.allDocuments;
      return this.allDocuments.filter(d =>
        this.readFilter === 'unread' ? !d.is_read : d.is_read
      );
    },
    pendingAckDocs() {
      return this.allDocuments.filter(d => d.requires_acknowledgement && !d.is_acknowledged);
    },
    paginationPages() {
      const { current_page: cur, last_page: last } = this.meta;
      if (last <= 1) return [];
      if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
      const pages = [];
      if (cur <= 4) {
        pages.push(1, 2, 3, 4, 5, null, last);
      } else if (cur >= last - 3) {
        pages.push(1, null, last - 4, last - 3, last - 2, last - 1, last);
      } else {
        pages.push(1, null, cur - 1, cur, cur + 1, null, last);
      }
      return pages;
    },
  },

  mounted() {
    this.fetchDocuments();
    this.fetchCategories();
  },

  methods: {
    async fetchDocuments() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/documents', {
          params: { page: this.page, length: 20, search: this.search, category_id: this.categoryId },
        });
        this.documents = data.data?.data || [];
        this.meta = {
          current_page: data.data?.current_page || 1,
          last_page:    data.data?.last_page    || 1,
          total:        data.data?.total        || 0,
        };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load documents');
      } finally {
        this.loading = false;
      }
    },

    async fetchCategories() {
      try {
        const { data } = await axios.get('/hr/documents/categories', { params: { all: true } });
        this.categories = data.data || [];
      } catch { /* silent */ }
    },

    goPage(p) { this.page = p; this.fetchDocuments(); },
    resetAndFetch() { this.page = 1; this.fetchDocuments(); },

    debounceSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.fetchDocuments(); }, 400);
    },

    openDoc(doc) {
      this.viewer = { open: true, doc: { ...doc }, ackNote: '', acking: false };
      if (!doc.is_read) {
        this.markRead(doc, true); // mark silently
      }
    },

    async downloadDoc(doc) {
      try {
        const res  = await axios.get(`/hr/self-service/documents/${doc.id}/download`, { responseType: 'arraybuffer' });
        const blob = new Blob([res.data], { type: 'application/octet-stream' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = doc.file_name || 'document';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true }));
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
      } catch (e) {
        this.$toast?.error('Download failed');
      }
      // Update local read state regardless of download outcome
      doc.is_read = true;
      if (this.viewer.doc?.id === doc.id) this.viewer.doc.is_read = true;
    },

    async markRead(doc, silent = false) {
      try {
        await axios.post(`/hr/self-service/documents/${doc.id}/read`);
        // Update doc in list
        const item = this.documents.find(d => d.id === doc.id);
        if (item) item.is_read = true;
        if (this.viewer.doc?.id === doc.id) this.viewer.doc.is_read = true;
        if (!silent) this.$toast?.success('Marked as read');
      } catch { /* silent */ }
    },

    async acknowledge(doc) {
      this.viewer.acking = true;
      try {
        await axios.post(`/hr/self-service/documents/${doc.id}/acknowledge`, { note: this.viewer.ackNote });
        const item = this.documents.find(d => d.id === doc.id);
        if (item) { item.is_acknowledged = true; item.is_read = true; }
        if (this.viewer.doc?.id === doc.id) { this.viewer.doc.is_acknowledged = true; this.viewer.doc.is_read = true; }
        this.$toast?.success('Document acknowledged');
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to acknowledge');
      } finally {
        this.viewer.acking = false;
      }
    },

    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    isExpired(d) { return d && new Date(d) < new Date(); },
    fileIcon(mime) {
      if (!mime) return 'file-alt';
      if (mime.includes('pdf')) return 'file-pdf';
      if (mime.includes('word') || mime.includes('document')) return 'file-word';
      if (mime.includes('excel') || mime.includes('sheet')) return 'file-excel';
      if (mime.includes('presentation') || mime.includes('powerpoint')) return 'file-powerpoint';
      if (mime.includes('image')) return 'file-image';
      return 'file-alt';
    },
    iconBg(mime) {
      if (!mime) return 'icon-bg--blue';
      if (mime.includes('pdf')) return 'icon-bg--red';
      if (mime.includes('word') || mime.includes('document')) return 'icon-bg--blue';
      if (mime.includes('excel') || mime.includes('sheet')) return 'icon-bg--green';
      if (mime.includes('image')) return 'icon-bg--purple';
      return 'icon-bg--blue';
    },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }

.filter-bar    { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { flex:1; min-width:180px; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 10px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; outline:none; }

.ack-alert { display:flex; align-items:center; gap:10px; padding:12px 16px; background:#fef9c3; border:1px solid #fde047; border-radius:10px; font-size:13px; color:#92400e; margin-bottom:14px; }

.doc-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(260px,1fr)); gap:14px; }
.doc-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px; cursor:pointer; transition:box-shadow .15s, border-color .15s; display:flex; flex-direction:column; gap:10px; position:relative; }
.doc-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.08); border-color:#c7d8f0; }
.doc-card--unread { border-left:3px solid #004AAD; }

.doc-card-icon { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.icon-bg--blue   { background:#dbeafe; color:#1d4ed8; }
.icon-bg--red    { background:#fee2e2; color:#dc2626; }
.icon-bg--green  { background:#dcfce7; color:#16a34a; }
.icon-bg--purple { background:#ede9fe; color:#7c3aed; }

.doc-card-body { flex:1; min-width:0; }
.doc-card-title { font-size:14px; font-weight:700; color:#1a2b45; line-height:1.3; margin-bottom:5px; }
.doc-card-meta  { display:flex; align-items:center; gap:6px; }
.doc-category   { font-size:11px; color:#7a90b0; }
.doc-ver        { font-size:10px; font-weight:700; background:#f1f5f9; color:#64748b; padding:1px 6px; border-radius:20px; }
.doc-card-date  { font-size:11px; color:#94a3b8; margin-top:4px; }

.doc-card-badges { display:flex; flex-wrap:wrap; gap:4px; }
.badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:20px; }
.badge--blue  { background:#dbeafe; color:#1d4ed8; }
.badge--amber { background:#fef9c3; color:#ca8a04; }
.badge--green { background:#dcfce7; color:#16a34a; }
.badge--gray  { background:#f1f5f9; color:#64748b; }
.badge--red   { background:#fee2e2; color:#dc2626; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:16px 0 4px; }
.pg-total    { font-size:13px; color:#7a90b0; }
.pg-controls { display:flex; align-items:center; gap:4px; }
.pg-btn      { min-width:34px; height:34px; padding:0 6px; background:#fff; border:1px solid #dde5f0; border-radius:7px; font-size:13px; font-weight:500; color:#1a2b45; cursor:pointer; transition:background .12s, border-color .12s; }
.pg-btn:hover:not(:disabled):not(.pg-btn--active) { background:#f5f8ff; border-color:#c7d8f0; }
.pg-btn:disabled  { opacity:.4; cursor:not-allowed; }
.pg-btn--active   { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }
.pg-ellipsis { min-width:28px; text-align:center; font-size:13px; color:#94a3b8; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-box          { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:14px; }
.empty-illustration { width:96px; height:96px; border-radius:50%; background:#f0f7ff; display:flex; align-items:center; justify-content:center; }
.empty-icon         { font-size:40px; color:#004AAD; opacity:.5; }
.empty-title        { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.empty-sub          { font-size:13px; color:#7a90b0; margin:0; text-align:center; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:620px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-head-left { display:flex; align-items:center; gap:12px; }
.modal-doc-icon  { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.modal-subtitle { font-size:11px; color:#94a3b8; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:16px; }

.detail-grid  { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.detail-item  { display:flex; flex-direction:column; gap:3px; }
.detail-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; }
.detail-value { font-size:13px; color:#1a2b45; }
.text-danger  { color:#dc2626; }

.desc-box    { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:12px 14px; }
.desc-content { font-size:13px; color:#1a2b45; margin:0; line-height:1.6; }

.viewer-actions { display:flex; gap:10px; flex-wrap:wrap; }

.ack-box   { background:#fef9c3; border:1px solid #fde047; border-radius:10px; padding:16px; }
.ack-title { font-size:14px; font-weight:700; color:#92400e; margin:0 0 6px; }
.ack-body  { font-size:13px; color:#92400e; margin:0 0 10px; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
</style>
